<template><div><code-well-header>
  <dt-stack direction="row" align="center" gap="500">
    <dt-avatar size="lg">
      <template #icon="{ iconSize }">
        <dt-icon-user :size="iconSize" />
      </template>
    </dt-avatar>
    <dt-avatar size="lg" full-name="dp" color="1000" presence="busy" />
    <dt-avatar size="lg" image-src="/assets/images/person.png" image-alt="avatar user" presence="active" />
  </dt-stack>
</code-well-header>
<!-- <component-combinator component-name="DtAvatar" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Avatar component is designed to prioritize different sources for content display. It will sequentially check for the availability of an image source (<code v-pre>image-src</code>) or content through the icon slot. If both are not provided, the avatar will extract and display initials from the full name (<code v-pre>full-name</code>). The resulting initials are extracted using the following logic:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If the string contains two or more words, the result will be the first character of the first and last word capitalized. E.g.:
<code v-pre>full-name: &quot;Jaqueline Nackos&quot;</code> will result in: <code v-pre>JN</code>.</li>
<li class="d-docsite--list-element">If the string contains only one word, the result will be the first two characters capitalized. E.g:
<code v-pre>full-name: &quot;Jaqueline&quot;</code> will result in: <code v-pre>JA</code>.
<code v-pre>full-name: &quot;10&quot;</code> will result in: <code v-pre>10</code>.</li>
<li class="d-docsite--list-element">If full-name is not provided, the result will be an empty string.</li>
</ul>
<table class="d-table dialtone-doc-table d-mb16">
    <colgroup>
        <col class="d-w64">
        <col>
        <col>
        <col>
    </colgroup>
    <thead>
        <tr>
            <th colspan="2">Type</th>
            <th>Usage</th>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td>
                <dt-avatar>
                  <template #icon="{ iconSize }">
                    <dt-icon-user :size="iconSize" />
                  </template>
                </dt-avatar>
            </td>
            <th class="d-ta-left"><a class="d-link" href="#icon">Icon</a></th>
            <td>When no username can be associated with the Avatar.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar full-name="DP" color="1600" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#initials">Initials</a></th>
            <td>When the user's name is known.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar image-alt="user avatar" image-src="/assets/images/person.png" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#image">Image</a></th>
            <td>When a custom image has been uploaded.</td>
        </tr>
        <tr>
            <td>
                <dt-avatar image-src="/assets/images/person.png" image-alt="person avatar" :group="3" />
            </td>
            <th class="d-ta-left"><a class="d-link" href="#group">Group</a></th>
            <td>When reflecting more than 2 participants.</td>
        </tr>
    </tbody>
</table>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="icon" tabindex="-1" class="d-docsite--header-3">Icon <a class="header-anchor d-link d-docsite--link d-link" href="#icon" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-avatar>
    <template #icon="{ iconSize }">
      <dt-icon-user :size="iconSize" />
    </template>
  </dt-avatar>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--{$size}">
  <div class="d-avatar__canvas">
    <span class="d-avatar__icon">
      <svg>...</svg>
    </span>
  </div>
</div>'
vueCode='
<dt-avatar>
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
'
/>
<h3 id="initials" tabindex="-1" class="d-docsite--header-3">Initials <a class="header-anchor d-link d-docsite--link d-link" href="#initials" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If <code v-pre>color</code> is not provided, the avatar will display a random color. This can be deterministic, see <a href="#seeded" class="d-docsite--link d-link">seeded</a>. The default color '000' is not included in randomized colors and can only be set manually.</p>
<code-well-header>
    <dt-stack direction="row" gap="500" class="d-wmx50p d-fw-wrap">
      <dt-avatar v-for="color in colors" :color="color" full-name="DP" />
    </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--{$size} d-avatar--{$color}">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials">DP</span>
  </div>
</div>
'
vueCode='
<!-- colors 000 to 1800 are valid, note 000 is the default grey color. -->
<dt-avatar
  full-name="DP"
  color="100"
/>
'
/>
<h3 id="image" tabindex="-1" class="d-docsite--header-3">Image <a class="header-anchor d-link d-docsite--link d-link" href="#image" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If <code v-pre>image-src</code> is not provided, or if image fails to load, the avatar will fall back to the initials extracted from the <code v-pre>full-name</code>.</p>
<code-well-header>
  <dt-avatar image-src="/assets/images/person.png" image-alt="avatar user" />
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--{$size}">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/path/to/image" alt="avatar user" />
  </div>
</div>
'
vueCode='
<dt-avatar image-src="/assets/images/person.png" image-alt="avatar user" />
'
/>
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<code-well-header>
    <dt-stack direction="row" align="center" gap="500">
        <dt-avatar v-for="size in sizes" :size="size">
          <template #icon="{ iconSize }">
            <dt-icon-user :size="iconSize" />
          </template>
        </dt-avatar>
    </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--xs">
  <div class="d-avatar__canvas">
    <svg>...</svg>
  </div>
</div>
<div class="d-avatar d-avatar--sm">
  <div class="d-avatar__canvas">
    <svg>...</svg>
  </div>
</div>
<div class="d-avatar d-avatar--md">
  <div class="d-avatar__canvas">
    <svg>...</svg>
  </div>
</div>
<div class="d-avatar d-avatar--lg">
  <div class="d-avatar__canvas">
    <svg>...</svg>
  </div>
</div>
<div class="d-avatar d-avatar--xl">
  <div class="d-avatar__canvas">
    <svg>...</svg>
  </div>
</div>
'
vueCode='
<dt-avatar size="xs">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
<dt-avatar size="sm" icon-name="user">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
<dt-avatar size="md" icon-name="user">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
<dt-avatar size="lg" icon-name="user">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
<dt-avatar size="xl" icon-name="user">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
'
/>
<h3 id="group" tabindex="-1" class="d-docsite--header-3">Group <a class="header-anchor d-link d-docsite--link d-link" href="#group" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Adds a count badge and CSS mask to the avatar based on the number of digits (1, 2, or 3). Currently available only with the <code v-pre>xs</code> avatar size.</p>
<code-well-header>
    <dt-stack direction="row" align="center" gap="500">
        <dt-avatar :group="100" image-src="/assets/images/person.png" image-alt="Person Avatar" />
        <dt-avatar :group="11" image-src="/assets/images/person.png" image-alt="Person Avatar" />
        <dt-avatar :group="3" image-src="/assets/images/person.png" image-alt="Person Avatar" />
    </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--group d-avatar--group-digits-3">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/assets/images/person.png" alt="Person Avatar"/>
  </div>
  <span class="d-avatar__count"><span class="d-avatar__count-number">12</span></span>
</div>
<div class="d-avatar d-avatar--group d-avatar--group-digits-2">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/assets/images/person.png" alt="Person Avatar"/>
  </div>
  <span class="d-avatar__count"><span class="d-avatar__count-number">12</span></span>
</div>
<div class="d-avatar d-avatar--group">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/assets/images/person.png" alt="Person Avatar"/>
  </div>
  <span class="d-avatar__count"><span class="d-avatar__count-number">1</span></span>
</div>
'
vueCode='
<dt-avatar :group="100" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar :group="11" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar :group="3" image-src="/assets/images/person.png" image-alt="Person Avatar" />
'
/>
<h3 id="presence" tabindex="-1" class="d-docsite--header-3">Presence <a class="header-anchor d-link d-docsite--link d-link" href="#presence" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Positions the <RouteLink to="/components/presence.html" class="d-docsite--link d-link">Presence</RouteLink> component at each size and applies a CSS mask to the avatar.</p>
<code-well-header>
  <dt-stack gap="400">
      <dt-stack direction="row" align="center" gap="400">
          <dt-avatar size="xs" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
          <dt-avatar size="sm" presence="away" image-src="/assets/images/person.png" image-alt="Person Avatar" />
          <dt-avatar size="md" presence="busy" image-src="/assets/images/person.png" image-alt="Person Avatar" />
          <dt-avatar size="lg" presence="offline" image-src="/assets/images/person.png" image-alt="Person Avatar" />
          <dt-avatar size="xl" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
      </dt-stack>
      <dt-stack direction="row" align="center" gap="400">
          <dt-avatar size="xs" presence="active" color="1200" full-name="Test Name" />
          <dt-avatar size="sm" presence="away" color="500" full-name="William Steele" />
          <dt-avatar size="md" presence="busy" color="800" full-name="Frank Richard" />
          <dt-avatar size="lg" presence="offline" color="1200" full-name="John Hawkins" />
          <dt-avatar size="xl" presence="active" color="1500" full-name="Alice Edwards" />
      </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<!-- First set with images -->
<div class="d-avatar d-avatar--xs d-avatar--color-100 d-avatar--presence">
  <div class="d-avatar__canvas">
    <img
      class="d-avatar__image"
      src="/assets/images/person.png"
      alt="Person Avatar"
    />
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--active"></div>
  </div>
</div>
<div class="d-avatar d-avatar--sm d-avatar--color-1100 d-avatar--presence">
  <div class="d-avatar__canvas">
    <img
      class="d-avatar__image"
      src="/assets/images/person.png"
      alt="Person Avatar"
    />
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--away"></div>
  </div>
</div>
<div class="d-avatar d-avatar--md d-avatar--color-1300 d-avatar--presence">
  <div class="d-avatar__canvas">
    <img
      class="d-avatar__image"
      src="/assets/images/person.png"
      alt="Person Avatar"
    />
  </div>
  <div
    class="d-presence d-avatar__presence d-avatar__presence--md"
    role="status"
    aria-live="off"
  >
    <div class="d-presence__inner d-presence__inner--busy"></div>
  </div>
</div>
<div class="d-avatar d-avatar--lg d-avatar--color-1200 d-avatar--presence">
  <div class="d-avatar__canvas">
    <img
      class="d-avatar__image"
      src="/assets/images/person.png"
      alt="Person Avatar"
    />
  </div>
  <div
    class="d-presence d-avatar__presence d-avatar__presence--lg"
    role="status"
    aria-live="off"
  >
    <div class="d-presence__inner d-presence__inner--offline"></div>
  </div>
</div>
<div class="d-avatar d-avatar--xl d-avatar--color-900 d-avatar--presence">
  <div class="d-avatar__canvas">
    <img
      class="d-avatar__image"
      src="/assets/images/person.png"
      alt="Person Avatar"
    />
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--active"></div>
  </div>
</div>
<!-- Second set with initials -->
<div class="d-avatar d-avatar--xs d-avatar--color-1200 d-avatar--presence">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials"></span>
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--active"></div>
  </div>
</div>
<div class="d-avatar d-avatar--sm d-avatar--color-500 d-avatar--presence">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials">W</span>
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--away"></div>
  </div>
</div>
<div class="d-avatar d-avatar--md d-avatar--color-800 d-avatar--presence">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials">FR</span>
  </div>
  <div
    class="d-presence d-avatar__presence d-avatar__presence--md"
    role="status"
    aria-live="off"
  >
    <div class="d-presence__inner d-presence__inner--busy"></div>
  </div>
</div>
<div class="d-avatar d-avatar--lg d-avatar--color-1200 d-avatar--presence">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials">JH</span>
  </div>
  <div
    class="d-presence d-avatar__presence d-avatar__presence--lg"
    role="status"
    aria-live="off"
  >
    <div class="d-presence__inner d-presence__inner--offline"></div>
  </div>
</div>
<div class="d-avatar d-avatar--xl d-avatar--color-1500 d-avatar--presence">
  <div class="d-avatar__canvas">
    <span class="d-avatar__initials">AE</span>
  </div>
  <div class="d-presence d-avatar__presence" role="status" aria-live="off">
    <div class="d-presence__inner d-presence__inner--active"></div>
  </div>
</div>
'
vueCode='
<dt-avatar size="xs" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar size="sm" presence="away" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar size="md" presence="busy" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar size="lg" presence="offline" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar size="xl" presence="active" image-src="/assets/images/person.png" image-alt="Person Avatar" />
<dt-avatar size="xs" presence="active" color="1200" full-name="Test Name" />
<dt-avatar size="sm" presence="away" color="500" full-name="William Steele" />
<dt-avatar size="md" presence="busy" color="800" full-name="Frank Richard" />
<dt-avatar size="lg" presence="offline" color="1200" full-name="John Hawkins" />
<dt-avatar size="xl" presence="active" color="1500" full-name="Alice Edwards" />
'
/>
<h3 id="overlay" tabindex="-1" class="d-docsite--header-3">Overlay <a class="header-anchor d-link d-docsite--link d-link" href="#overlay" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" align="center" gap="500">
    <dt-avatar size="lg" image-src="/assets/images/person.png" image-alt="avatar user">
      <template #overlayIcon>
        <dt-icon-hear />
      </template>
    </dt-avatar>
    <dt-avatar size="lg" image-src="/assets/images/person.png" image-alt="avatar user" overlay-text="+3" />
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-avatar d-avatar--lg">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/path/to/image" alt="avatar user" />
  </div>
  <div class="d-avatar__overlay">...</div>
</div>
<div class="d-avatar d-avatar--lg">
  <div class="d-avatar__canvas">
    <img class="d-avatar__image" src="/path/to/image" alt="avatar user" />
  </div>
  <div class="d-avatar__overlay">
    <p class="d-avatar__overlay-text">+3</p>
  </div>
</div>'
vueCode='
<dt-avatar size="lg" image-src="/assets/images/person.png" image-alt="avatar user">
  <template #overlayIcon>
    <dt-icon-hear />
  </template>
</dt-avatar>
<dt-avatar size="lg" image-src="/assets/images/person.png" image-alt="avatar user" overlay-text="+3" />
'
/>
<h3 id="clickable" tabindex="-1" class="d-docsite--header-3">Clickable <a class="header-anchor d-link d-docsite--link d-link" href="#clickable" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">If you need to create a clickable avatar you can set the clickable prop. This will make the avatar a clickable component, set some styling and will be navigable by keyboard. In order for the clickable avatar to be fully accessible, you need to either set <code v-pre>full-name</code>, <code v-pre>image-alt</code> or <code v-pre>icon-aria-label</code> attributes.</p>
<code-well-header>
  <dt-stack direction="row" align="center" gap="500">
    <dt-avatar clickable icon-aria-label="user" ref="example-clickable">
      <template #icon="{ iconSize }">
        <dt-icon-user :size="iconSize" />
      </template>
    </dt-avatar>
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs['example-clickable']"
vueCode='
<dt-avatar clickable icon-aria-label="user">
  <template #icon="{ iconSize }">
    <dt-icon-user :size="iconSize" />
  </template>
</dt-avatar>
'
/>
<h3 id="seeded" tabindex="-1" class="d-docsite--header-3">Seeded <a class="header-anchor d-link d-docsite--link d-link" href="#seeded" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You may use a seed to make the randomly generated color be the same every time for that seed. This is useful if you want users to always have the same color, just pass in their unique user ID as the seed.</p>
<code-well-header>
  <dt-stack direction="row" align="center" gap="500">
    <dt-avatar full-name="Jaqueline Nackos" seed="X5G3D7D3DS3WL7" ref="example-seeded" />
  </dt-stack>
</code-well-header>
<code-example-tabs
:htmlCode="() => $refs['example-seeded']"
vueCode='
<dt-avatar full-name="Jaqueline Nackos" seed="X5G3D7D3DS3WL7" />
'
/>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="avatar" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="avatar"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Initial avatars' background and font color combinations have been paired to ensure minimum contrast is met.</p>
<p class="d-docsite--paragraph">When it comes to voiceover, avatars accompanying a label should generally be considered decorative,
is not focusable, nor is it read out. An example is a user's avatar next to their name.</p>
<p class="d-docsite--paragraph">Avatars unaccompanied by labels, especially those representing functionality or navigation, should be focusable and
read out in voiceover. Please refer
to <a href="https://www.w3.org/WAI/tutorials/images/decorative" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">WCAG</a> references for
your specific usage.</p>
</div></template>


<script setup>
import { DtIconUser, DtIconHear } from '@dialpad/dialtone-icons/vue3';

const colors = ['100', '200', '300', '400', '500', '600', '700', '800', '900', '1000', '1100', '1200', '1300', '1400', '1500', '1600', '1700', '1800'];
const sizes = ['xs', 'sm', 'md', 'lg', 'xl'];
</script>